<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import type { CategoryInterface } from "~~/server/types/Categories";
import type { FilterModel } from "~~/server/types/Products";
import { restoreProductListPosition } from "~/composables/useProductListPosition";
import { showCatalogFilters } from "~/utils/catalogFilterVisibility";
import CategoryDescription from "~/components/products/CategoryDescription.vue";
import CatalogPagination from "~/components/products/CatalogPagination.vue";
import { catalogCanonicalPath } from "~/utils/catalogPagination";
import {
  emptyProductCatalog,
  fetchProductCatalog,
} from "~/composables/useProductCatalog";

type QueryValue = string | string[] | null | undefined;
type TreeKey = string | number;

type TreeV2Instance = {
  getCheckedKeys: (leafOnly?: boolean) => TreeKey[];
  setCheckedKeys: (keys: TreeKey[]) => void;
  setChecked: (key: TreeKey, checked: boolean, deep?: boolean) => void;
};

const props = withDefaults(defineProps<{
  categorySlug?: string;
}>(), {
  categorySlug: "",
});

const ProductGridTemplate1 = defineAsyncComponent(() =>
  import("~/components/products/productGridTemplate1.vue")
);
const ProductGridTemplate2 = defineAsyncComponent(() =>
  import("~/components/products/productGridTemplate2.vue")
);
const FilterByAttributes = defineAsyncComponent(() =>
  import("~/components/filterByAttributesComponent.vue")
);
const FilterByPrice = defineAsyncComponent(() =>
  import("~/components/filterByPrice.vue")
);
const SectionComponent = defineAsyncComponent(() =>
  import("~/components/SectionsComponent.vue")
);

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { isMobile } = useDevice();
const productsStore = useProductStore();
const { settings: storefrontSettings } = storeToRefs(useSettingsStore());
const { categories_tree: allCategoryTrees } = storeToRefs(productsStore);

const catTreeRef = ref<TreeV2Instance>();
const loadingFilter = ref(true);
const showFilter = ref(false);
const showAllSelectedCategories = ref(false);
const isCatOpen = ref(false);
const priceFilterKey = ref(0);
const perPage = 24;

const defaultFilter = (): FilterModel => ({
  categories: [],
  brands: [],
  search: "",
  options: {},
  price: [],
});

const defaultSort = computed(() => {
  return {
    discounts: "discount:desc",
    populars: "views:desc",
    latest: "id:desc",
  }[props.categorySlug] || "id:desc";
});

const getQueryValue = (value: QueryValue): string => {
  if (Array.isArray(value)) {
    return value[0] || "";
  }

  return value || "";
};

const parseNumberList = (value: QueryValue): number[] => {
  const raw = getQueryValue(value);

  if (!raw) {
    return [];
  }

  return raw
    .split(",")
    .map(item => Number(item))
    .filter(item => Number.isInteger(item) && item > 0);
};

const parseStringList = (value: QueryValue): string[] => {
  const raw = getQueryValue(value);

  if (!raw) {
    return [];
  }

  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item).trim()).filter(Boolean);
      }
    } catch {
      return [];
    }
  }

  return raw.split(",").map(item => item.trim()).filter(Boolean);
};

const parsePrice = (value: QueryValue): number[] => {
  const prices = getQueryValue(value)
    .split(",")
    .map(item => Number(item))
    .filter(item => Number.isFinite(item) && item >= 0);

  return prices.length === 2 ? prices : [];
};

const parseOptions = (value: QueryValue): Record<string, number[]> => {
  const raw = getQueryValue(value);

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, number[]>>((result, [key, values]) => {
      if (!Array.isArray(values)) {
        return result;
      }

      const normalizedValues = values
        .map(item => Number(item))
        .filter(item => Number.isInteger(item) && item > 0);

      if (normalizedValues.length) {
        result[key] = normalizedValues;
      }

      return result;
    }, {});
  } catch {
    return {};
  }
};

const getPageFromQuery = (): number => {
  const parsedPage = Number(getQueryValue(route.query.page));

  return Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
};

const getSortFromQuery = (): string => {
  const requestedSort = getQueryValue(route.query.sort);
  const allowedSorts = new Set([
    "price:asc",
    "price:desc",
    "id:asc",
    "id:desc",
    "views:asc",
    "views:desc",
    "discount:asc",
    "discount:desc",
  ]);

  return allowedSorts.has(requestedSort) ? requestedSort : defaultSort.value;
};

const buildFilterFromQuery = (): FilterModel => {
  const result = defaultFilter();

  result.categories = parseNumberList(route.query.categories);
  result.brands = parseStringList(route.query.brands);
  result.search = getQueryValue(route.query.sq).trim();
  result.options = parseOptions(route.query.options);
  result.price = parsePrice(route.query.price);

  const legacyOptionId = Number(getQueryValue(route.query.option_id));
  const legacyOptionValueId = Number(getQueryValue(route.query.option_value_id));

  if (
    Number.isInteger(legacyOptionId)
    && legacyOptionId > 0
    && Number.isInteger(legacyOptionValueId)
    && legacyOptionValueId > 0
  ) {
    result.options[String(legacyOptionId)] = [legacyOptionValueId];
  }

  return result;
};

const routeState = computed(() => ({
  filter: buildFilterFromQuery(),
  page: getPageFromQuery(),
  sort: getSortFromQuery(),
}));
const initialRouteState = routeState.value;
const filter = ref<FilterModel>(initialRouteState.filter);
const page = ref(initialRouteState.page);
const sortKey = ref(initialRouteState.sort);
const searchInput = ref(filter.value.search);

const catalogRequest = computed(() => {
  const { filter: queryFilter, page: queryPage, sort: querySort } = routeState.value;
  const selectedPrice = queryFilter.price;

  return {
    page: queryPage,
    per_page: perPage,
    category_slug: props.categorySlug || undefined,
    q: queryFilter.search || undefined,
    categories: queryFilter.categories.length ? queryFilter.categories.join(",") : undefined,
    brands: queryFilter.brands.length ? JSON.stringify(queryFilter.brands) : undefined,
    options: Object.keys(queryFilter.options).length ? JSON.stringify(queryFilter.options) : undefined,
    min_price: selectedPrice.length === 2 ? selectedPrice[0] : undefined,
    max_price: selectedPrice.length === 2 ? selectedPrice[1] : undefined,
    sort: querySort,
  };
});

const catalogKey = computed(() => `product-catalog:${locale.value}:${route.fullPath}`);
const { data: catalog, status, error } = await useAsyncData(
  catalogKey,
  () => fetchProductCatalog(catalogRequest.value),
  { default: () => emptyProductCatalog(perPage) },
);

if (error.value) {
  showError(error.value);
}

const products = computed(() => catalog.value?.list ?? []);
const productOptions = computed(() => catalog.value?.product_options ?? []);
const meta = computed(() => catalog.value?.meta ?? emptyProductCatalog(perPage).meta);
const facets = computed(() => catalog.value?.facets ?? emptyProductCatalog(perPage).facets);
const category = computed(() => catalog.value?.category ?? null);

const findCategoryById = (
  categories: CategoryInterface[],
  id: number,
): CategoryInterface | null => {
  for (const item of categories) {
    if (item.id === id) {
      return item;
    }

    if (item.children?.length) {
      const found = findCategoryById(item.children, id);

      if (found) {
        return found;
      }
    }
  }

  return null;
};

const categoryTrees = computed(() => {
  if (!category.value?.id) {
    return allCategoryTrees.value;
  }

  const selectedTree = findCategoryById(allCategoryTrees.value, category.value.id);

  return selectedTree ? [selectedTree] : [];
});

const hasActiveFilters = computed(() => Boolean(
  filter.value.categories.length
  || filter.value.brands.length
  || filter.value.search
  || Object.keys(filter.value.options || {}).length
  || filter.value.price.length,
));

const hasFilterColumn = computed(() => {
  return showCatalogFilters(category.value, storefrontSettings.value.search_show_filters);
});

watch(hasFilterColumn, enabled => {
  if (!enabled) {
    showFilter.value = false;
  }
});

const hasCategoryFilter = computed(() => {
  if (!category.value?.id) {
    return categoryTrees.value.length > 0;
  }

  return Boolean(categoryTrees.value[0]?.children?.length);
});

const calcHeight = computed(() => {
  const categoryCount = category.value?.id
    ? (categoryTrees.value[0]?.children?.length || 0) + 1
    : categoryTrees.value.length;
  const closedHeight = Math.min(categoryCount * 27, 250);

  return isCatOpen.value ? 300 : Math.max(closedHeight, 100);
});

const getCategoryName = (id: number): string => {
  return productsStore.categories
    .find(item => Number(item.id) === Number(id))?.name ?? "";
};

const setCategoryFilter = () => {
  filter.value.categories = (catTreeRef.value?.getCheckedKeys(true) || [])
    .map(key => Number(key))
    .filter(key => Number.isInteger(key) && key > 0);
};

const syncTreeFromFilter = async () => {
  await nextTick();
  catTreeRef.value?.setCheckedKeys(filter.value.categories || []);
};

const removeCategory = (categoryId: number) => {
  catTreeRef.value?.setChecked(Number(categoryId), false, false);
  setCategoryFilter();
};

const createQueryFromState = () => ({
  ...route.query,
  sq: filter.value.search || undefined,
  categories: filter.value.categories.length ? filter.value.categories.join(",") : undefined,
  brands: filter.value.brands.length ? JSON.stringify(filter.value.brands) : undefined,
  options: Object.keys(filter.value.options || {}).length
    ? JSON.stringify(filter.value.options)
    : undefined,
  price: filter.value.price.length === 2 ? filter.value.price.join(",") : undefined,
  sort: sortKey.value === defaultSort.value ? undefined : sortKey.value,
  page: page.value > 1 ? String(page.value) : undefined,
  option_id: undefined,
  option_value_id: undefined,
  search: undefined,
});

const isApplyingQuery = ref(false);
const saveStateToUrl = useDebounceFn(async () => {
  if (!isApplyingQuery.value) {
    await router.replace({ query: createQueryFromState() });
  }
}, 150);

const clearFilters = async () => {
  filter.value = defaultFilter();
  searchInput.value = "";
  page.value = 1;
  priceFilterKey.value++;
  showAllSelectedCategories.value = false;

  await syncTreeFromFilter();

  if (isMobile) {
    showFilter.value = false;
  }
};

watch(
  routeState,
  async (nextRouteState) => {
    isApplyingQuery.value = true;
    filter.value = nextRouteState.filter;
    searchInput.value = filter.value.search;
    page.value = nextRouteState.page;
    sortKey.value = nextRouteState.sort;

    await syncTreeFromFilter();
    isApplyingQuery.value = false;
  },
);

watch(
  filter,
  () => {
    if (isApplyingQuery.value) {
      return;
    }

    page.value = 1;
    void saveStateToUrl();
  },
  { deep: true },
);

watch(sortKey, () => {
  if (isApplyingQuery.value) {
    return;
  }

  page.value = 1;
  void saveStateToUrl();
});

const runSearch = useDebounceFn((value: string) => {
  const normalizedValue = value.trim();

  if (filter.value.search !== normalizedValue) {
    filter.value.search = normalizedValue;
  }
}, 250);

watch(searchInput, value => runSearch(value));

onMounted(async () => {
  const listPath = route.fullPath;

  void Promise.allSettled([
    productsStore.loadCategories(),
    productsStore.loadOptions(),
  ]).finally(async () => {
    loadingFilter.value = false;
    await syncTreeFromFilter();
  });

  await nextTick();
  await restoreProductListPosition(listPath);
});

usePageSeo(computed(() => ({
  icon: category.value?.icon ?? null,
  photo: category.value?.photo ?? null,
  name: category.value?.name || t("productsCatalog"),
  short_description: category.value?.short_description ?? null,
  schemaType: "website",
  canonicalPath: catalogCanonicalPath(route.path, route.query.page),
  indexable: !getQueryValue(route.query.sq).trim(),
})));
</script>

<template>
  <section class="container-xxl">
    <div class="row">
      <ClientOnly v-if="hasFilterColumn">
        <div class="col-lg-3 col-12 filter" :class="{ 'd-none': !showFilter && isMobile }">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-2">
            <h4 class="m-0">{{ $t("filter") }}</h4>

            <el-button
              type="primary"
              size="small"
              round
              class="search-btn"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              {{ $t("clear") }}
            </el-button>
          </div>

          <div class="filter-box mb-3">
            <div class="header mb-2">
              <icon name="material-symbols-light:search" />
              {{ $t("search") }}
            </div>
            <el-input class="custom-input-products" v-model="searchInput" clearable :placeholder="$t('search')" />
          </div>

          <FilterByPrice
            v-if="facets.price.max > 0"
            :key="priceFilterKey"
            v-model:filter="filter"
            :products="products"
            :min-price="facets.price.min"
            :max-price="facets.price.max"
          />

          <div v-if="hasCategoryFilter" v-loading="loadingFilter" class="filter-box mb-3">
            <div class="header border-bottom pb-2 mb-2">
              <icon name="material-symbols-light:folder-copy-outline" />
              {{ $t("categories") }}
            </div>

            <el-tree-v2
              v-if="categoryTrees.length"
              ref="catTreeRef"
              :height="calcHeight"
              :data="categoryTrees"
              :show-checkbox="true"
              :default-checked-keys="filter.categories"
              :default-expanded-keys="category?.id ? [category.id] : []"
              :props="{ label: 'name', children: 'children', value: 'id' }"
              :expand-on-click-node="true"
              @node-expand="isCatOpen = true"
              @node-collapse="isCatOpen = false"
              @check="setCategoryFilter"
            />
          </div>

          <FilterByAttributes
            v-if="facets.brands.length || facets.option_value_ids.length"
            v-model:filter="filter"
            :brands="facets.brands"
            :available-option-value-ids="facets.option_value_ids"
          />

          <div v-if="isMobile" class="text-center">
            <el-button class="my-4" round type="primary" @click="showFilter = false">
              {{ $t("results") }}
            </el-button>
          </div>
        </div>

        <template #fallback>
          <div class="col-lg-3 d-none d-lg-block">
            <el-skeleton :rows="8" animated />
          </div>
        </template>
      </ClientOnly>

      <div
        class="col-12"
        :class="{
          'col-lg-9': hasFilterColumn,
          'd-none': hasFilterColumn && showFilter && isMobile,
        }"
      >
        <h1 v-if="category?.name" class="mb-5 section_title">
          {{ category.name }}
        </h1>

        <SectionComponent
          v-if="category?.section"
          :section="category.section"
        />

        <CategoryDescription
          v-if="category?.description"
          :key="category.id"
          :description="category.description"
        />

        <div
          class="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom"
          :class="{ 'mt-3': category }"
        >
          <h1 v-if="!category" class="catalog-title m-0">{{ $t("productsCatalog") }}</h1>
          <span v-else />

          <div class="d-flex flex-wrap align-items-center justify-content-end gap-2">
            <el-button v-if="!hasFilterColumn && hasActiveFilters" text @click="clearFilters">
              {{ $t("clearFilters") }}
            </el-button>
            <el-select
              v-model="sortKey"
              :aria-label="$t('sortByPrice')"
              style="width: 200px"
              size="small"
              class="custom-sort-select"
            >
              <el-option value="price:asc" :label="'↑ ' + $t('sortByPrice')" />
              <el-option value="price:desc" :label="'↓ ' + $t('sortByPrice')" />
              <el-option value="id:asc" :label="'↑ ' + $t('sortByDate')" />
              <el-option value="id:desc" :label="'↓ ' + $t('sortByDate')" />
              <el-option value="views:asc" :label="'↑ ' + $t('sortByPopular')" />
              <el-option value="views:desc" :label="'↓ ' + $t('sortByPopular')" />
              <el-option value="discount:asc" :label="'↑ ' + $t('sortByDiscount')" />
              <el-option value="discount:desc" :label="'↓ ' + $t('sortByDiscount')" />
            </el-select>
          </div>
        </div>

        <div
          v-if="(hasFilterColumn && (hasCategoryFilter || isMobile)) || (hasCategoryFilter && filter.categories.length > 0)"
          class="row align-items-start justify-content-between filter-values pb-lg-2 pb-3"
        >
          <div v-if="hasCategoryFilter && (hasFilterColumn || filter.categories.length > 0)" class="col-8 pb-2">
            <div class="d-flex align-items-center">
              <icon name="material-symbols-light:folder-copy-outline" class="me-1" />
              <el-text class="label fs-0 me-2">{{ $t("categories") }}:</el-text>

              <el-tag
                v-for="categoryId in showAllSelectedCategories
                  ? filter.categories
                  : filter.categories.slice(0, 3)"
                :key="categoryId"
                type="primary"
                size="small"
                class="mx-1"
                round
                closable
                @close="removeCategory(categoryId)"
              >
                {{ getCategoryName(categoryId) }}
              </el-tag>

              <el-button
                v-if="filter.categories.length > 3"
                text
                size="small"
                @click="showAllSelectedCategories = !showAllSelectedCategories"
              >
                {{ showAllSelectedCategories ? "Скрий" : `Още (${filter.categories.length - 3})` }}
              </el-button>

              <el-tag v-if="!filter.categories.length" type="primary" size="default" round effect="dark">
                {{ category?.name || $t("all_categories") }}
              </el-tag>
            </div>
          </div>

          <div v-if="isMobile && hasFilterColumn && !showFilter" class="col-4 d-flex justify-content-end">
            <el-button
              class="custom-margin-filter-button"
              size="small"
              type="primary"
              round
              @click="showFilter = true"
            >
              {{ $t("filter") }}
            </el-button>
          </div>
        </div>

        <div
          v-loading="status === 'pending' && products.length > 0"
          class="product-list-container"
          :aria-busy="status === 'pending'"
        >
          <template v-if="products.length">
            <ProductGridTemplate2
              v-if="category?.design === 'template2'"
              :list="products"
            />
            <ProductGridTemplate1
              v-else
              :list="products"
              :all-options="productOptions"
            />
          </template>

          <el-skeleton v-else-if="status === 'pending'" :rows="8" animated />
          <el-empty v-else :description="$t('no_products_found')" />

          <div v-if="meta.total" class="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
            <el-text>{{ $t("total") }}: {{ meta.total }}</el-text>

            <CatalogPagination :current-page="meta.current_page" :last-page="meta.last_page" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.catalog-title {
  font-size: var(--fs-lg);
}
.custom-input-products {
  :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 2px #888888 inset !important;
    border-radius: 20px !important;
  }
  :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 2px var(--el-color-info) inset;
  }
}
.custom-sort-select {
  :deep(.el-select__wrapper) {
    border: 2px solid var(--el-color-info) !important;
    border-radius: 12px !important;
    box-shadow: none !important;

    &:hover {
      border-color: var(--el-border-color) !important;
    }
  }

  &:deep(.is-focus .el-select__wrapper) {
    border-color: var(--el-border-color) !important;
    box-shadow: none !important;
  }
}

</style>
